import { createAsyncThunk } from '@reduxjs/toolkit';

import { EMPTY_ARRAY } from 'store/constants';
import { RequestResponseExceptionType, post, put } from 'utils/AjaxUtil';
import AppConstants from 'utils/AppConstants';
import DataUrlConstants from 'utils/DataUrlConstants';
import { makeUrl, MakeUrlTokens } from 'utils/UrlUtil';

import { CustomActionPythonPayload, SaveCustomActionPythonResponse, SaveCustomActionPythonRejected } from './types';

export interface CustomActionPythonTestingPayload extends Partial<CustomActionPythonPayload> {
  variableValues?: Array<{ name?: string; value?: string }>;
  inputValues?: Record<string, any>;
  instanceId?: string;
  functionId?: string;
  testId?: string;
}

const makeFormDataPayload = (params: CustomActionPythonPayload | CustomActionPythonTestingPayload) => {
  const reqPayload = new FormData();

  // For save/create/update, send all fields
  params.id && reqPayload.append('id', params.id);
  reqPayload.append('displayName', params.displayName || '');
  reqPayload.append('description', params.description || '');
  reqPayload.append('apiName', params.apiName || '');
  reqPayload.append('helpText', params.helpText || '');

  // Python specific fields
  reqPayload.append('code', params.code || '');
  reqPayload.append('requirements', params.requirements || '');

  // Input variables. Accept both boolean and string forms for required/multivalued
  // because callers may pre-coerce to boolean (e.g. makeCustomActionPythonPayload).
  if (params.inputVariables) {
    reqPayload.append(
      'inputVariables',
      JSON.stringify(
        params.inputVariables?.map((variable) => {
          const multivalued =
            (variable.multivalued as unknown) === AppConstants.TRUE || (variable.multivalued as unknown) === true;
          const required =
            (variable.required as unknown) === AppConstants.TRUE || (variable.required as unknown) === true;
          return { ...variable, multivalued, required };
        }) || EMPTY_ARRAY
      )
    );
  }

  // Test cases
  if (params.tests) {
    reqPayload.append('tests', JSON.stringify(params.tests || EMPTY_ARRAY));
  }

  reqPayload.append('tags', JSON.stringify(params.tags || EMPTY_ARRAY));

  if (typeof params.iconPath !== 'string' && params.iconPath) {
    reqPayload.append('icon', params.iconPath as any);
  }

  return reqPayload;
};

export interface SaveCustomActionPythonParams extends CustomActionPythonPayload {
  instanceId: string;
}

export const saveCustomActionPython = createAsyncThunk<
  SaveCustomActionPythonResponse,
  SaveCustomActionPythonParams,
  { rejectValue: SaveCustomActionPythonRejected }
>('custom-action-python/saveCustomActionPython', async (params, { rejectWithValue }) => {
  try {
    const { instanceId, ...actionData } = params;

    // Use UPDATE (PUT) if action has an ID, otherwise CREATE (POST)
    if (actionData.id) {
      return (
        await put<SaveCustomActionPythonResponse>(
          makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_UPDATE_DRAFT, { functionId: actionData.id } as MakeUrlTokens),
          makeFormDataPayload(actionData)
        )
      ).data;
    } else {
      return (
        await post<SaveCustomActionPythonResponse>(
          makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_CREATE_DRAFT),
          makeFormDataPayload(actionData)
        )
      ).data;
    }
  } catch (error) {
    return rejectWithValue({
      message: (error as RequestResponseExceptionType).response.data?.message,
    });
  }
});

export interface ValidateCustomActionPythonParams extends CustomActionPythonPayload {
  instanceId: string;
}

export const testCustomActionPython = createAsyncThunk<
  SaveCustomActionPythonResponse,
  CustomActionPythonTestingPayload,
  { rejectValue: SaveCustomActionPythonRejected }
>('custom-action-python/testingCustomActionPython', async (params, { rejectWithValue }) => {
  try {
    const { functionId, testId, variableValues } = params;

    if (!functionId || !testId) {
      return rejectWithValue({
        message: 'Function ID and Test ID are required to run tests',
      });
    }

    // Send only variableValues in the payload
    const payload = new FormData();
    if (variableValues) {
      payload.append('variableValues', JSON.stringify(variableValues));
    }

    return (
      await post<SaveCustomActionPythonResponse>(
        makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_TESTING, { functionId, testId } as MakeUrlTokens),
        payload
      )
    ).data;
  } catch (error) {
    const data = (error as any)?.response?.data;
    if (data?.response) {
      return data;
    }

    return rejectWithValue({
      message: (error as RequestResponseExceptionType).response?.data?.message,
    });
  }
});
